import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/ui/PageContainer';
import { Modal, Dialog } from '@/components/ui/Modal';
import { ListingBlock, type ListingBlockAction } from '@/components/ui/ListingBlock';
import { Select } from '@/components/ui/Select/Select';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { ClientsService, type ClientDTO } from '@/services/ClientsService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { Plus, Calendar, MapPin, Users, ArrowLeft, Mail, ChevronDown } from 'lucide-react';

export function ClientEvents() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const clientsSvc = useMemo(() => new ClientsService(), []);
  const eventsSvc = useMemo(() => new EventsService(), []);
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);
  
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [events, setEvents] = useState<EventDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventDTO>({ 
    id: '', 
    client_id: clientId || '', 
    name: '', 
    slug: '' 
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Filter values matching FilterValues interface from RefineSearchModal
  type FilterValues = {
    min_rooms?: number | null;
    min_conference_capacity?: number | null;
    star_rating?: number | null;
    max_airport_distance_km?: number | null;
    has_spa?: boolean | null;
    min_restaurants?: number | null;
    min_bars?: number | null;
    min_pools?: number | null;
    beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
    all_inclusive?: boolean | null;
    property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
    has_golf?: boolean | null;
    min_tripadvisor_rating?: number | null;
  };
  
  const [filters, setFilters] = useState<FilterValues>({});

  async function load() {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const [clientData, eventsData] = await Promise.all([
        clientsSvc.get(clientId),
        eventsSvc.listByClient(clientId)
      ]);
      setClient(clientData);
      setEvents(eventsData);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [clientId]);
  
  // Check for editEvent in navigation state and open edit modal
  useEffect(() => {
    const editEvent = (location.state as any)?.editEvent as EventDTO | undefined;
    if (editEvent && editEvent.client_id === clientId) {
      // Clear the state to prevent reopening on re-render
      window.history.replaceState({}, document.title);
      
      // Open edit modal with the event
      setEditingId(editEvent.id);
      setForm({ ...editEvent });
      
      // Load filters from event, or default to empty
      const eventFilters = editEvent.venue_filters || {};
      setFilters({
        min_rooms: eventFilters.min_rooms ?? null,
        min_conference_capacity: eventFilters.min_conference_capacity ?? null,
        star_rating: eventFilters.star_rating ?? null,
        max_airport_distance_km: eventFilters.max_airport_distance_km ?? null,
        has_spa: eventFilters.has_spa ?? null,
        min_restaurants: eventFilters.min_restaurants ?? null,
        min_bars: eventFilters.min_bars ?? null,
        min_pools: eventFilters.min_pools ?? null,
        beach_access: eventFilters.beach_access ?? null,
        all_inclusive: eventFilters.all_inclusive ?? null,
        property_type: eventFilters.property_type ?? null,
        has_golf: eventFilters.has_golf ?? null,
        min_tripadvisor_rating: eventFilters.min_tripadvisor_rating ?? null,
      });
      // Auto-expand if filters exist
      const hasFilters = Object.values(eventFilters).some(v => v !== null && v !== undefined);
      setFiltersExpanded(hasFilters);
      setModalOpen(true);
    }
  }, [location.state, clientId]);

  function openCreate() {
    setEditingId(null);
    setForm({ id: '', client_id: clientId || '', name: '', slug: '' });
    setFilters({});
    setFiltersExpanded(false);
    setModalOpen(true);
  }

  function openEdit(event: EventDTO) {
    setEditingId(event.id);
    setForm({ ...event });
    // Load filters from event, or default to empty
    const eventFilters = event.venue_filters || {};
    setFilters({
      min_rooms: eventFilters.min_rooms ?? null,
      min_conference_capacity: eventFilters.min_conference_capacity ?? null,
      star_rating: eventFilters.star_rating ?? null,
      max_airport_distance_km: eventFilters.max_airport_distance_km ?? null,
      has_spa: eventFilters.has_spa ?? null,
      min_restaurants: eventFilters.min_restaurants ?? null,
      min_bars: eventFilters.min_bars ?? null,
      min_pools: eventFilters.min_pools ?? null,
      beach_access: eventFilters.beach_access ?? null,
      all_inclusive: eventFilters.all_inclusive ?? null,
      property_type: eventFilters.property_type ?? null,
      has_golf: eventFilters.has_golf ?? null,
      min_tripadvisor_rating: eventFilters.min_tripadvisor_rating ?? null,
    });
    // Auto-expand if filters exist
    const hasFilters = Object.values(eventFilters).some(v => v !== null && v !== undefined);
    setFiltersExpanded(hasFilters);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm({ id: '', client_id: clientId || '', name: '', slug: '' });
    setFilters({});
    setFiltersExpanded(false);
  }
  
  function updateFilter<K extends keyof FilterValues>(key: K, value: FilterValues[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  async function submit() {
    if (!form.name.trim()) {
      notifyError('Event name is required');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare venue_filters - only include non-null values
      const venueFilters: Record<string, any> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          venueFilters[key] = value;
        }
      });
      const venueFiltersToSave = Object.keys(venueFilters).length > 0 ? venueFilters : null;
      
      if (editingId) {
        await eventsSvc.update(editingId, { 
          name: form.name, 
          description: form.description || undefined,
          venue_filters: venueFiltersToSave
        });
        notifySuccess('Event updated successfully');
        closeModal();
        await load();
      } else {
        const result = await eventsSvc.create(clientId!, {
          name: form.name,
          description: form.description || undefined,
          venue_filters: venueFiltersToSave
        });
        notifySuccess('Event created successfully');
        closeModal();
        
        // Navigate to the new event's attendees page
        navigate(`/organization/events/${result.id}/attendees`);
      }
    } catch (e: any) {
      notifyError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    try {
      setEvents(prev => prev.filter(e => e.id !== id));
      await eventsSvc.delete(id);
      notifySuccess('Event deleted successfully');
      setDeleteConfirmId(null);
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Delete failed');
    }
  }

  const filteredEvents = events.filter(event => 
    searchTerm === '' || 
    event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !client) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>

      <PageHeader 
        title={client?.name || 'Client Events'} 
        description="Manage events for this client"
      >
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/organization/clients')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Clients
          </Button>
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            New Event
          </Button>
        </div>
      </PageHeader>


      {/* Search Bar */}
      <div className="mb-6">
        <Input
          placeholder="Search events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {/* Events Grid */}
      {filteredEvents.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No events found' : 'No events yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Create your first event for this client'}
            </p>
            {!searchTerm && (
              <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
                New Event
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvents.map((event) => {
            // Prepare actions - Edit removed, only available from Event Dashboard
            const actions: ListingBlockAction[] = [
              {
                label: 'Delete',
                onClick: (e) => { e.stopPropagation(); setDeleteConfirmId(event.id); }
              }
            ];

            return (
              <ListingBlock
                key={event.id}
                title={event.name}
                subtitle={`/${event.slug}`}
                description={event.description || undefined}
                badge={{
                  label: `ID: ${event.id}`,
                  variant: 'secondary'
                }}
                actions={actions}
                onClick={() => navigate(`/organization/events/${event.id}`)}
              />
            );
          })}
        </div>
      )}

      {/* Event Modal */}
      <Modal 
        isOpen={modalOpen} 
        onClose={closeModal} 
        title={editingId ? 'Edit Event' : 'New Event'}
        description={editingId ? 'Update event information' : 'Create a new event for this client'}
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Event Name *</label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} 
              placeholder="e.g., 2024 Annual Company Retreat"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea 
              value={form.description || ''} 
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} 
              placeholder="Brief description of the event"
              rows={3}
            />
          </div>
          
          {/* Venue Filters Section - Collapsible */}
          <div className="border rounded-lg">
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              <h4 className="text-sm font-medium text-gray-900">Venue Filters (Optional)</h4>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform ${
                  filtersExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {filtersExpanded && (
              <div className="p-4 pt-0 space-y-4 border-t">
                <p className="text-xs text-gray-600 mb-4">
                  These filters will pre-populate when using the research/proposal tool (OpenNeo) for this event.
                </p>
                
                {/* Min Rooms */}
                <Input
                  label="Minimum Rooms"
                  type="number"
                  min={0}
                  value={filters.min_rooms ?? ''}
                  onChange={(e) =>
                    updateFilter('min_rooms', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 80"
                />

                {/* Conference Capacity & Star Rating */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Min Conference Capacity"
                    type="number"
                    min={0}
                    value={filters.min_conference_capacity ?? ''}
                    onChange={(e) =>
                      updateFilter('min_conference_capacity', e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="e.g., 200"
                  />
                  <div>
                    <label htmlFor="star-rating" className="block text-sm font-medium mb-1">
                      Minimum Star Rating
                    </label>
                    <Select
                      id="star-rating"
                      value={filters.star_rating?.toString() || ''}
                      onChange={(e) =>
                        updateFilter('star_rating', e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      <option value="">Any</option>
                      <option value="3">3+ Stars</option>
                      <option value="4">4+ Stars</option>
                      <option value="5">5 Stars</option>
                    </Select>
                  </div>
                </div>

                {/* Airport Distance */}
                <Input
                  label="Max Airport Distance (km)"
                  type="number"
                  min={0}
                  value={filters.max_airport_distance_km ?? ''}
                  onChange={(e) =>
                    updateFilter('max_airport_distance_km', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 30"
                />

                {/* Amenities */}
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-900">Amenities</h5>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="has_spa"
                        checked={filters.has_spa === true}
                        onChange={(e) => updateFilter('has_spa', e.target.checked ? true : null)}
                      />
                      <label htmlFor="has_spa" className="text-sm text-gray-700 cursor-pointer">
                        Spa Facilities
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="all_inclusive"
                        checked={filters.all_inclusive === true}
                        onChange={(e) => updateFilter('all_inclusive', e.target.checked ? true : null)}
                      />
                      <label htmlFor="all_inclusive" className="text-sm text-gray-700 cursor-pointer">
                        All-Inclusive Available
                      </label>
                    </div>
                  </div>

                  {/* Count-Based Amenities */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Input
                      label="Minimum Restaurants"
                      type="number"
                      min={0}
                      value={filters.min_restaurants ?? ''}
                      onChange={(e) =>
                        updateFilter('min_restaurants', e.target.value ? Number(e.target.value) : null)
                      }
                      placeholder="e.g., 2"
                    />

                    <Input
                      label="Minimum Bars/Lounges"
                      type="number"
                      min={0}
                      value={filters.min_bars ?? ''}
                      onChange={(e) =>
                        updateFilter('min_bars', e.target.value ? Number(e.target.value) : null)
                      }
                      placeholder="e.g., 1"
                    />

                    <Input
                      label="Minimum Pools"
                      type="number"
                      min={0}
                      value={filters.min_pools ?? ''}
                      onChange={(e) =>
                        updateFilter('min_pools', e.target.value ? Number(e.target.value) : null)
                      }
                      placeholder="e.g., 1"
                    />

                    <div>
                      <label htmlFor="min_tripadvisor_rating" className="block text-sm font-medium text-gray-700 mb-1">
                        Min TripAdvisor Rating
                      </label>
                      <Select
                        id="min_tripadvisor_rating"
                        value={filters.min_tripadvisor_rating?.toString() || ''}
                        onChange={(e) =>
                          updateFilter('min_tripadvisor_rating', e.target.value ? Number(e.target.value) : null)
                        }
                      >
                        <option value="">Any rating</option>
                        <option value="3.0">3.0+ stars</option>
                        <option value="3.5">3.5+ stars</option>
                        <option value="4.0">4.0+ stars</option>
                        <option value="4.5">4.5+ stars</option>
                        <option value="5.0">5.0 stars</option>
                      </Select>
                    </div>
                  </div>

                  {/* Enum-Based Amenities */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label htmlFor="beach_access" className="block text-sm font-medium text-gray-700 mb-1">
                        Beach Access
                      </label>
                      <Select
                        id="beach_access"
                        value={filters.beach_access || ''}
                        onChange={(e) => updateFilter('beach_access', (e.target.value as any) || null)}
                      >
                        <option value="">Any</option>
                        <option value="beachfront">Beachfront</option>
                        <option value="beach_access">Beach Access</option>
                        <option value="none">No Beach Access</option>
                      </Select>
                    </div>

                    <div>
                      <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-1">
                        Property Type
                      </label>
                      <Select
                        id="property_type"
                        value={filters.property_type || ''}
                        onChange={(e) => updateFilter('property_type', (e.target.value as any) || null)}
                      >
                        <option value="">Any</option>
                        <option value="adults_only">Adults Only</option>
                        <option value="family_friendly">Family Friendly</option>
                        <option value="mixed">Mixed</option>
                      </Select>
                    </div>

                    <div>
                      <label htmlFor="has_golf" className="block text-sm font-medium text-gray-700 mb-1">
                        Golf Course
                      </label>
                      <Select
                        id="has_golf"
                        value={filters.has_golf === true ? 'yes' : ''}
                        onChange={(e) => updateFilter('has_golf', e.target.value === 'yes' ? true : null)}
                      >
                        <option value="">Any</option>
                        <option value="yes">On-site or Nearby</option>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={saving}>
              {editingId ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        isOpen={!!deleteConfirmId} 
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && remove(deleteConfirmId)}
        title="Delete Event" 
        description="Are you sure you want to delete this event? This will also delete all associated attendees. This action cannot be undone."
        confirmText="Delete" 
        confirmVariant="danger"
      />
    </PageContainer>
  );
}
